/**
 * 주요 상담 교환 발췌 / 축어록 후보 추천 (DOCUMENT_OUTPUTS §3-V, AI_PROMPTS §8의 규칙 기반 버전).
 *
 * - 짧은 발췌(exchange): 상담자 개입 → 내담자 반응이 오간 몇 턴 (실제 발언 그대로)
 * - 축어록(verbatim): 약 10~15분 구간, 시작·끝 시각, 선택 이유, 수퍼비전 질문
 * 극적인 내용이 아니라 "개입과 반응을 검토할 가치"를 기준으로 고른다.
 * 화자 판정이 불확실한 줄이 있으면 확인 필요로 표시한다 (원음·전사 확인 후 사용).
 */
import { countKeyPoints, keyPointScore, keyPointsOf, keyPointSummary, type KeyPointKind } from './keyPoints';

export type ExcerptSegment = {
  segmentId: string;
  startMs: number;
  clinicalRole: 'counselor' | 'client' | 'unknown' | 'mixed';
  roleConfidence: number;
  counselorAct?: string;
  isBackchannel: boolean;
  text: string;
  verificationStatus?: string;
  clinicalPriority?: string;
};

export type ExcerptLine = {
  segmentId: string;
  startMs: number;
  role: ExcerptSegment['clinicalRole'];
  text: string;
  uncertain: boolean;
  /** 이 줄의 핵심 포인트 (고를 때 화면에만 표시, A32) */
  points?: KeyPointKind[];
};

export type ExcerptCandidate = {
  /** 구간의 핵심 포인트 합계 (A32) */
  points?: Partial<Record<KeyPointKind, number>>;
  kind: 'exchange' | 'verbatim';
  startMs: number;
  endMs: number;
  segmentIds: string[];
  lines: ExcerptLine[];
  score: number;
  reasons: string[];
  supervisionQuestions: string[];
  needsCheck: boolean;
  hasRisk: boolean;
};

const EMOTION =
  /(불안|무섭|두려|힘들|서운|억울|답답|부끄러|외로|막막|초조|짜증|지쳤|죄책감|우울|피곤|무기력|허전|속상|위축|화가|화나|슬펐|슬프|눈물|울컥|괴롭|상처|미안|죄송|걱정|염려|혼란|후회|창피|수치|분노|미워|싫어|좋아|기쁘|감사|행복|안심|편안|설레|긴장|불편|무력|절망|자책|혐오|요동|마음이|감정|느낌)/;

/** 상담자 개입 종류를 말투로 추정 (화자 판정에서 개입 종류가 없을 때) */
function actFromText(text: string): string | undefined {
  const t = text.trim();
  if (/(군요|네요|구나|셨겠어요|으시겠어요|것 같네요|것 같아요)[.!…]*$/.test(t)) return 'reflection';
  if (/\?$|(나요|까요|가요|세요|어요|아요|죠|습니까|니까)\?*$/.test(t) && /(어떤|어떻게|무엇|뭐|왜|언제|누구|어디|얼마나|어땠|어떠|있으세요|있었어요|드셨|하셨|\?)/.test(t)) return 'question';
  return undefined;
}
const INTERVENTION = new Set(['reflection', 'interpretation', 'question', 'restatement', 'validation']);

function lineOf(s: ExcerptSegment, technique?: RegExp): ExcerptLine {
  const points = keyPointsOf({ role: s.clinicalRole, text: s.text }, technique);
  return {
    segmentId: s.segmentId,
    startMs: s.startMs,
    role: s.clinicalRole,
    text: s.text,
    ...(points.length ? { points } : {}),
    uncertain:
      s.clinicalRole === 'unknown' ||
      s.clinicalRole === 'mixed' ||
      s.roleConfidence < 0.75 ||
      s.verificationStatus === 'needs-review',
  };
}

function keywordHits(text: string, keywords: string[]): string[] {
  return keywords.filter((k) => k.length >= 2 && text.includes(k));
}

/** 가설 문장에서 비교용 핵심어 추출 (2글자 이상 명사성 토큰, 흔한 말 제외) */
export function hypothesisKeywords(hypotheses: string[]): string[] {
  const STOP = new Set(['있다', '한다', '패턴', '경향', '상황', '때문', '관련', '생각', '마음', '사람']);
  const out = new Set<string>();
  for (const h of hypotheses)
    for (const w of h.split(/[\s,.()]+/)) {
      const core = w.replace(/(이|가|을|를|은|는|에|에서|으로|로|과|와|의|도|만|하고|한다|하는|된다|되는)$/, '');
      if (core.length >= 2 && !STOP.has(core)) out.add(core);
    }
  return [...out];
}

function scoreWindow(segs: ExcerptSegment[], keywords: string[], technique?: RegExp) {
  let score = 0;
  const reasons: string[] = [];
  let pairs = 0;
  let emotions = 0;
  const hits = new Set<string>();
  let risk = false;
  let unknown = 0;
  const acts = new Map<string, number>();
  let disclosures = 0;
  for (let k = 0; k < segs.length; k++) {
    const s0 = segs[k]!;
    const s = s0.clinicalRole === 'counselor' && !s0.counselorAct ? { ...s0, counselorAct: actFromText(s0.text) } : s0;
    if (s.clinicalRole === 'client' && s.text.length >= 40) disclosures++;
    if (s.clinicalPriority === 'critical') risk = true;
    if (s.clinicalRole === 'unknown' || s.clinicalRole === 'mixed') unknown++;
    if (s.clinicalRole === 'counselor' && s.counselorAct) acts.set(s.counselorAct, (acts.get(s.counselorAct) ?? 0) + 1);
    if (s.clinicalRole === 'counselor' && s.counselorAct && INTERVENTION.has(s.counselorAct)) {
      const reply = segs.slice(k + 1).find((x) => !x.isBackchannel);
      if (reply?.clinicalRole === 'client') {
        pairs++;
        score += s.counselorAct === 'reflection' || s.counselorAct === 'interpretation' ? 2.5 : 1.5;
      }
    }
    if (s.clinicalRole === 'client' && EMOTION.test(s.text)) {
      emotions++;
      score += 1;
    }
    for (const h of keywordHits(s.text, keywords)) hits.add(h);
  }
  score += Math.min(3, hits.size) * 1.5;
  score += Math.min(2, disclosures * 0.5);
  // 위험 신호는 점수에 넣지 않는다 (지도감독: 위험 구간이 아니라 치료 진행·알아차림·변화가 드러나는 구간을 고른다)
  const points = countKeyPoints(segs.map((s) => ({ role: s.clinicalRole, text: s.text })), technique);
  score += keyPointScore(points);
  score -= unknown;
  if (pairs) reasons.push(`상담자 개입→내담자 반응 ${pairs}회`);
  for (const [a, n] of acts) {
    const label: Record<string, string> = {
      reflection: '반영',
      interpretation: '해석',
      question: '질문',
      validation: '타당화',
      'self-disclosure': '자기개방',
      structuring: '구조화',
    };
    if (label[a]) reasons.push(`${label[a]} ${n}회`);
  }
  if (emotions) reasons.push(`내담자 정서 표현 ${emotions}회`);
  if (disclosures) reasons.push(`내담자의 긴 이야기 ${disclosures}회`);
  if (hits.size) reasons.push(`사례개념화 관련: ${[...hits].slice(0, 4).join(', ')}`);
  if (risk) reasons.push('위험 관련 내용 포함');
  const summary = keyPointSummary(points);
  if (summary) reasons.unshift(`핵심 포인트: ${summary}`);
  return { score, reasons, acts, emotions, risk, unknown, points };
}

function questions(r: ReturnType<typeof scoreWindow>): string[] {
  const q: string[] = [];
  if (r.acts.get('interpretation')) q.push('상담자의 해석이 내담자의 경험보다 앞서거나 유도적이지 않았는가?');
  if (r.emotions) q.push('내담자의 정서 표현에 상담자가 충분히 머물고 반영했는가?');
  if (r.acts.get('self-disclosure')) q.push('상담자의 자기개방이 내담자에게 어떤 영향을 주었는가?');
  if (r.risk) q.push('위험 관련 진술에 대한 탐색과 안전 확인은 충분했는가?');
  if (r.acts.get('question') && !r.acts.get('reflection'))
    q.push('질문이 이어질 때 내담자의 말을 반영하는 개입이 부족하지 않았는가?');
  if (!q.length) q.push('이 구간에서 상담자의 개입 의도와 내담자의 반응은 어떻게 연결되는가?');
  return q;
}

export function recommendExchanges(
  all: ExcerptSegment[],
  opts: { hypotheses?: string[]; max?: number } = {},
): ExcerptCandidate[] {
  const segs = all.filter((s) => !s.isBackchannel);
  const keywords = hypothesisKeywords(opts.hypotheses ?? []);
  const cands: ExcerptCandidate[] = [];
  for (let i = 0; i < segs.length; i++) {
    if (segs[i]!.clinicalRole !== 'counselor') continue; // 상담자 개입에서 시작
    for (const len of [4, 6, 8]) {
      const w = segs.slice(i, i + len);
      if (w.length < 4) continue;
      if (w.at(-1)!.startMs - w[0]!.startMs > 180_000) continue;
      const roles = new Set(w.map((s) => s.clinicalRole));
      if (!roles.has('counselor') || !roles.has('client')) continue;
      const r = scoreWindow(w, keywords);
      if (r.score <= 0) continue;
      const lines = w.map((s) => lineOf(s));
      cands.push({
        kind: 'exchange',
        startMs: w[0]!.startMs,
        endMs: w.at(-1)!.startMs,
        segmentIds: w.map((s) => s.segmentId),
        lines,
        score: Number((r.score / Math.sqrt(len)).toFixed(2)),
        reasons: r.score > 2 ? r.reasons : ['참고 후보 (두드러진 신호는 적음)', ...r.reasons],
        supervisionQuestions: questions(r),
        needsCheck: lines.some((l) => l.uncertain),
        hasRisk: r.risk,
        points: r.points,
      });
    }
  }
  // 기준(점수 2 초과)을 넘는 구간 우선, 모자라면 점수가 높은 참고 후보로 최소 3개까지 채운다
  const max = opts.max ?? 5;
  const strong = pickNonOverlapping(cands.filter((c) => c.score * 1 > 0 && !c.reasons[0]!.startsWith('참고')), max);
  if (strong.length >= Math.min(3, max)) return strong;
  const rest = pickNonOverlapping(
    cands.filter((c) => !strong.some((o) => c.startMs <= o.endMs && o.startMs <= c.endMs)),
    Math.min(3, max) - strong.length,
  );
  return [...strong, ...rest].sort((a, b) => a.startMs - b.startMs);
}

export function recommendVerbatims(
  all: ExcerptSegment[],
  opts: { hypotheses?: string[]; minutes?: number; max?: number; /** 채택한 접근의 기법 말투 (A33) */ technique?: RegExp } = {},
): ExcerptCandidate[] {
  const segs = all.filter((s) => !s.isBackchannel);
  if (!segs.length) return [];
  const keywords = hypothesisKeywords(opts.hypotheses ?? []);
  const span = (opts.minutes ?? 12) * 60_000;
  const end = segs.at(-1)!.startMs;
  const cands: ExcerptCandidate[] = [];
  for (let t = segs[0]!.startMs; t + span * 0.8 <= end || t === segs[0]!.startMs; t += 60_000) {
    const w = segs.filter((s) => s.startMs >= t && s.startMs < t + span);
    if (w.length < 8) continue;
    const r = scoreWindow(w, keywords, opts.technique);
    const lines = w.map((s) => lineOf(s, opts.technique));
    const uncertainShare = lines.filter((l) => l.uncertain).length / lines.length;
    cands.push({
      kind: 'verbatim',
      startMs: w[0]!.startMs,
      endMs: w.at(-1)!.startMs,
      segmentIds: w.map((s) => s.segmentId),
      lines,
      score: Number((r.score - uncertainShare * 5).toFixed(2)),
      reasons: r.reasons,
      supervisionQuestions: questions(r),
      needsCheck: uncertainShare > 0,
      hasRisk: r.risk,
      points: r.points,
    });
    if (t + span > end) break;
  }
  return pickNonOverlapping(cands, opts.max ?? 3);
}

function pickNonOverlapping(cands: ExcerptCandidate[], max: number): ExcerptCandidate[] {
  const out: ExcerptCandidate[] = [];
  for (const c of [...cands].sort((a, b) => b.score - a.score)) {
    if (out.some((o) => c.startMs <= o.endMs && o.startMs <= c.endMs)) continue;
    out.push(c);
    if (out.length >= max) break;
  }
  return out.sort((a, b) => a.startMs - b.startMs);
}
